import { Anchor, Badge, Button, Group, NativeSelect, Pagination, Paper, Stack, Tabs, Text, TextInput } from "@mantine/core"
import { useEffect, useState } from "react"
import { IconArrowLeft, IconChartDots, IconSearch, appIcon } from "atlas_forecasting_app/components/common/icons"
import { EmptyState, ErrorAlert, LoadingState, PageHeader, SectionCard, CenteredMessage } from "atlas_forecasting_app/components/common/page_elements"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { formatDateTime } from "atlas_forecasting_app/lib/format"
import { RouterLink } from "atlas_forecasting_app/lib/router"

const GRAPH_MODE_OPTIONS = [
  { value: "history", label: "History over time" },
  { value: "latest", label: "Latest forecast only" },
]

const SCHEDULE_UNIT_OPTIONS = [
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "months", label: "Months" },
]

function buildDraft(item) {
  return {
    graph_mode: item.graph_mode || "history",
    schedule_every_value: item.schedule_every_value != null ? String(item.schedule_every_value) : "",
    schedule_every_unit: item.schedule_every_unit || "",
  }
}

function minuteLabel(minute) {
  return `Runs at :${String(minute).padStart(2, "0")} past the hour.`
}

export function AdminFeaturedQuestionsPage({ currentUser }) {
  const isAdmin = !!currentUser?.roles?.includes("admin")
  const [featuredQuestions, setFeaturedQuestions] = useState([])
  const [candidates, setCandidates] = useState([])
  const [drafts, setDrafts] = useState({})
  const [query, setQuery] = useState("")
  const [activeTab, setActiveTab] = useState("manage")
  const [candidatePage, setCandidatePage] = useState(1)
  const [candidateTotalPages, setCandidateTotalPages] = useState(1)
  const [loadingFeatured, setLoadingFeatured] = useState(true)
  const [searching, setSearching] = useState(true)
  const [savingId, setSavingId] = useState(null)
  const [movingId, setMovingId] = useState(null)
  const [addingQuestionId, setAddingQuestionId] = useState(null)
  const [removingId, setRemovingId] = useState(null)
  const [error, setError] = useState("")

  const loadFeaturedQuestions = async () => {
    if (!isAdmin) return

    setLoadingFeatured(true)
    try {
      const data = await apiFetch("/api/admin/featured_questions")
      const items = data.featured_questions || []
      setFeaturedQuestions(items)
      setDrafts(items.reduce((memo, item) => ({ ...memo, [item.id]: buildDraft(item) }), {}))
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setLoadingFeatured(false)
    }
  }

  const loadCandidates = async ({ nextQuery = query, page = candidatePage } = {}) => {
    if (!isAdmin) return

    setSearching(true)
    try {
      const qs = new URLSearchParams()
      qs.set("per_page", "10")
      qs.set("page", String(page))
      if (nextQuery.trim()) qs.set("q", nextQuery.trim())
      const data = await apiFetch(`/api/admin/featured_questions/candidates?${qs.toString()}`)
      setCandidates(data.questions || [])
      setCandidatePage(data.page || page)
      setCandidateTotalPages(data.total_pages || 1)
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setSearching(false)
    }
  }

  useEffect(() => {
    loadFeaturedQuestions()
    loadCandidates({ nextQuery: "", page: 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin])

  useEffect(() => {
    if (loadingFeatured) return
    if (featuredQuestions.length === 0 && activeTab === "manage") setActiveTab("choose")
  }, [activeTab, featuredQuestions.length, loadingFeatured])

  const updateDraft = (featuredQuestionId, updates) => {
    setDrafts((prev) => ({
      ...prev,
      [featuredQuestionId]: {
        ...(prev[featuredQuestionId] || {}),
        ...updates,
      },
    }))
  }

  const addFeaturedQuestion = async (questionId) => {
    setAddingQuestionId(questionId)
    try {
      await apiFetch("/api/admin/featured_questions", {
        method: "POST",
        body: { question_id: questionId },
      })
      await Promise.all([loadFeaturedQuestions(), loadCandidates({ nextQuery: query, page: candidatePage })])
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setAddingQuestionId(null)
    }
  }

  const saveFeaturedQuestion = async (featuredQuestionId) => {
    const draft = drafts[featuredQuestionId] || {}
    setSavingId(featuredQuestionId)

    try {
      await apiFetch(`/api/admin/featured_questions/${featuredQuestionId}`, {
        method: "PATCH",
        body: {
          graph_mode: draft.graph_mode,
          schedule_every_value: draft.schedule_every_value,
          schedule_every_unit: draft.schedule_every_unit,
        },
      })
      await loadFeaturedQuestions()
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setSavingId(null)
    }
  }

  const moveFeaturedQuestion = async (featuredQuestionId, direction) => {
    setMovingId(featuredQuestionId)
    try {
      const data = await apiFetch(`/api/admin/featured_questions/${featuredQuestionId}/reorder`, {
        method: "PATCH",
        body: { direction },
      })
      const items = data.featured_questions || []
      setFeaturedQuestions(items)
      setDrafts(items.reduce((memo, item) => ({ ...memo, [item.id]: buildDraft(item) }), {}))
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setMovingId(null)
    }
  }

  const removeFeaturedQuestion = async (featuredQuestionId) => {
    setRemovingId(featuredQuestionId)
    try {
      const data = await apiFetch(`/api/admin/featured_questions/${featuredQuestionId}`, { method: "DELETE", body: {} })
      const items = data.featured_questions || []
      setFeaturedQuestions(items)
      setDrafts(items.reduce((memo, item) => ({ ...memo, [item.id]: buildDraft(item) }), {}))
      await loadCandidates({ nextQuery: query, page: candidatePage })
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setRemovingId(null)
    }
  }

  if (!currentUser) {
    return <CenteredMessage title="Admin" body="Log in required." />
  }

  if (!isAdmin) {
    return <CenteredMessage title="Admin" body="You don’t have access to this page." />
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Featured questions"
        subtitle="Choose which public questions appear on the homepage and how they refresh."
        action={
          <Button component={RouterLink} to="/admin" variant="light" leftSection={appIcon(IconArrowLeft, "inline")}>
            Back to admin
          </Button>
        }
      />

      <ErrorAlert message={error} />

      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || "manage")}>
        <Tabs.List>
          <Tabs.Tab value="choose">Choose Featured Questions</Tabs.Tab>
          <Tabs.Tab value="manage">Manage Featured Questions</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="choose" pt="md">
          <SectionCard title="Choose featured questions" subtitle="Search public questions that are not already featured.">
            <Group align="flex-end" gap="sm">
              <TextInput
                label="Search public questions"
                placeholder="Search question titles..."
                value={query}
                onChange={(event) => setQuery(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") loadCandidates({ nextQuery: query, page: 1 })
                }}
                style={{ flex: 1, maxWidth: 560 }}
              />
              <Button
                type="button"
                leftSection={appIcon(IconSearch, "inline")}
                onClick={() => loadCandidates({ nextQuery: query, page: 1 })}
              >
                Search
              </Button>
            </Group>

            {searching ? <LoadingState label="Loading public questions..." /> : null}
            {!searching && candidates.length === 0 ? <EmptyState message="No matching public questions are available to feature." /> : null}
            {!searching ? (
              <Stack gap="sm">
                {candidates.map((question) => (
                  <Paper key={question.id} withBorder radius="md" p="md" data-candidate-question-id={question.id}>
                    <Group justify="space-between" align="flex-start" gap="md">
                      <Stack gap={4}>
                        <Anchor component={RouterLink} to={`/questions/${question.id}`} underline="never" fw={600}>
                          {question.title}
                        </Anchor>
                        <Text size="sm" c="dimmed">
                          Public {question.question_type} question
                        </Text>
                      </Stack>
                      <Button
                        type="button"
                        variant="light"
                        disabled={addingQuestionId === question.id}
                        onClick={() => addFeaturedQuestion(question.id)}
                      >
                        {addingQuestionId === question.id ? "Adding..." : "Add"}
                      </Button>
                    </Group>
                  </Paper>
                ))}

                {candidateTotalPages > 1 ? (
                  <Group justify="center">
                    <Pagination
                      value={candidatePage}
                      total={candidateTotalPages}
                      onChange={(page) => loadCandidates({ nextQuery: query, page })}
                    />
                  </Group>
                ) : null}
              </Stack>
            ) : null}
          </SectionCard>
        </Tabs.Panel>

        <Tabs.Panel value="manage" pt="md">
          <SectionCard title="Manage featured questions" subtitle="These cards appear on the homepage in the order shown here.">
            {loadingFeatured ? <LoadingState label="Loading featured questions..." /> : null}
            {!loadingFeatured && featuredQuestions.length === 0 ? <EmptyState message="No featured questions yet." /> : null}
            {!loadingFeatured ? (
              <Stack gap="md">
                {featuredQuestions.map((item, index) => {
                  const draft = drafts[item.id] || buildDraft(item)
                  const hasSchedule = draft.schedule_every_value.trim() !== "" || draft.schedule_every_unit !== ""

                  return (
                    <Paper key={item.id} withBorder radius="md" p="lg" data-featured-question-id={item.id}>
                      <Stack gap="md">
                        <Group justify="space-between" align="flex-start" gap="md">
                          <Stack gap={4}>
                            <Anchor component={RouterLink} to={`/questions/${item.question.id}`} underline="never" fw={600}>
                              {item.question.title}
                            </Anchor>
                            <Text size="sm" c="dimmed">
                              Position {index + 1} • Public {item.question.question_type} question
                            </Text>
                          </Stack>
                          <Badge variant="light" leftSection={appIcon(IconChartDots, "inline")}>
                            Homepage
                          </Badge>
                        </Group>

                        <Group align="flex-end" gap="sm">
                          <NativeSelect
                            label="Card graph"
                            data={GRAPH_MODE_OPTIONS}
                            value={draft.graph_mode}
                            onChange={(event) => updateDraft(item.id, { graph_mode: event.currentTarget.value || "history" })}
                            style={{ minWidth: 220 }}
                          />
                          <TextInput
                            label="Every"
                            placeholder="Leave blank to disable"
                            value={draft.schedule_every_value}
                            onChange={(event) => updateDraft(item.id, { schedule_every_value: event.currentTarget.value })}
                            style={{ width: 160 }}
                          />
                          <NativeSelect
                            label="Unit"
                            placeholder="Choose a unit"
                            data={[{ value: "", label: "Choose a unit" }, ...SCHEDULE_UNIT_OPTIONS]}
                            value={draft.schedule_every_unit}
                            onChange={(event) => updateDraft(item.id, { schedule_every_unit: event.currentTarget.value || "" })}
                            style={{ minWidth: 180 }}
                          />
                        </Group>

                        <Stack gap={2}>
                          <Text size="sm" c="dimmed">
                            {minuteLabel(item.schedule_minute_offset)}
                          </Text>
                          {item.next_due_at ? (
                            <Text size="sm" c="dimmed">
                              Next scheduled kickoff {formatDateTime(item.next_due_at)}
                            </Text>
                          ) : hasSchedule ? (
                            <Text size="sm" c="dimmed">
                              Save changes to activate this schedule.
                            </Text>
                          ) : (
                            <Text size="sm" c="dimmed">
                              Automated forecasts are currently disabled for this featured question.
                            </Text>
                          )}
                        </Stack>

                        <Group gap="sm">
                          <Button type="button" variant="light" disabled={index === 0 || movingId === item.id} onClick={() => moveFeaturedQuestion(item.id, "up")}>
                            {movingId === item.id ? "Moving..." : "Move up"}
                          </Button>
                          <Button
                            type="button"
                            variant="light"
                            disabled={index === featuredQuestions.length - 1 || movingId === item.id}
                            onClick={() => moveFeaturedQuestion(item.id, "down")}
                          >
                            {movingId === item.id ? "Moving..." : "Move down"}
                          </Button>
                          <Button type="button" disabled={savingId === item.id} onClick={() => saveFeaturedQuestion(item.id)}>
                            {savingId === item.id ? "Saving..." : "Save changes"}
                          </Button>
                          <Button
                            type="button"
                            color="red"
                            variant="light"
                            disabled={removingId === item.id}
                            onClick={() => removeFeaturedQuestion(item.id)}
                          >
                            {removingId === item.id ? "Removing..." : "Remove"}
                          </Button>
                        </Group>
                      </Stack>
                    </Paper>
                  )
                })}
              </Stack>
            ) : null}
          </SectionCard>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  )
}
